import React from "react";
import { Card, CardContent, Button } from "@mui/material"; // Keeping these imports if still needed for other components
import { MoveRight } from "lucide-react"; // Keeping this import if still needed
import iosAppImage from "../../../assets/AppDevelopment/ios.png";
import ios1AppImage from "../../../assets/AppDevelopment/ios1.png";
import ios2AppImage from "../../../assets/AppDevelopment/ios2.png";

import { motion, type Easing } from "framer-motion";
import { Helmet } from "react-helmet-async"; // For SEO

import {
  FaApple,
  FaMobileAlt,
  FaRocket,
  FaShieldAlt,
  FaCode,
  FaPalette,
  FaCogs,
  FaLightbulb,
  FaDollarSign,
  FaHeadset,
  FaQuestionCircle,
  FaUsers,
  FaTools,
} from "react-icons/fa"; // Added more icons for new sections
import { Link } from "react-router-dom";

// Animation variants (reusing from Android/Flutter for consistency)
const fadeInLeft = {
  hidden: { opacity: 0, x: -80 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.8 } },
};

const fadeInRight = {
  hidden: { opacity: 0, x: 80 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.8 } },
};

const fadeUp = {
  hidden: { opacity: 0, y: 60 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7 } },
};

const fadeInUp = {
  hidden: { opacity: 0, y: 50 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const fadeProcessStep = (index: number) => ({
  hidden: { opacity: 0, x: index % 2 === 0 ? -50 : 50 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.7,
      ease: [0, 0, 0.58, 1] as Easing,
    },
  },
});

const IosAppDevelopmentPage = () => {
  return (
    <>
      <Helmet>
        <title>Leading iOS App Development in Noida | Nadezhda Venture</title>
        <meta
          name="description"
          content="Expert iOS app development company in Noida. We build elegant, secure, and high-performance iPhone and iPad applications."
        />
        <meta
          name="keywords"
          content="iOS app development, iPhone app development Noida, iPad app development, custom iOS app, Apple app development, app development company"
        />
        <meta name="author" content="Nadezhda Venture" />

        {/* Open Graph for social sharing */}
        <meta property="og:title" content="Leading iOS App Development in Noida | Nadezhda Venture" />
        <meta property="og:description" content="Build premium, secure, and user-friendly iOS applications for your business." />
        <meta property="og:image" content="https://www.apple.com/v/ios/what-is/images/overview/hero_ios17__b5wjn6g8t3na_large.jpg" /> {/* iOS related image */}
        <meta property="og:url" content="https://yourdomain.com/ios-app-development" />
        <meta property="og:type" content="website" />
      </Helmet>

      <section className="bg-gradient-to-br from-black via-gray-900 to-gray-950 text-white relative overflow-hidden flex items-center justify-center h-[400px]">
  <img
    src="https://knovator.com/wp-content/uploads/2023/10/iOS-Develooer_banner.png"
    className="absolute inset-0 w-full h-full object-cover opacity-70 z-0"
    alt="iOS App Development Background"
  />

  <div className="absolute inset-0 opacity-30 z-10" />

  <motion.div
    className="relative z-20 max-w-4xl px-6 py-32 md:py-48 text-center md:text-left"
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true }}
    variants={fadeInLeft}
  >
    <h1 className="text-3xl md:text-5xl font-extrabold text-orange-500 mb-4">
      Elite iOS App Development Services
    </h1>
    <p className="text-lg md:text-xl text-gray-300 leading-relaxed">
      Crafting sophisticated, intuitive, and high-performance iOS applications that stand out on the App Store. We bring your vision to life on iPhone, iPad, and beyond.
    </p>
    <div className="mt-8">
      <Link to="/contact ">
  <button className="bg-orange-700 hover:bg-orange-500 px-6 py-3 rounded-lg font-semibold transition text-white">
    Get Started with iOS Development
  </button>
</Link>
    </div>
  </motion.div>
</section>


      {/* SECTION 2 - Why Choose iOS? */}
      <section className="bg-white text-white py-20 px-6">
        <div className="max-w-6xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-orange-500">
            Why Choose iOS for Your Business?
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                title: "Premium User Experience",
                desc: "iOS users expect and appreciate high-quality design and seamless performance, leading to higher engagement and loyalty.",
                icon: <FaApple size={50} className="text-red-400 mb-4 mx-auto" />,
              },
              {
                title: "Higher User Engagement",
                desc: "iOS users typically demonstrate higher engagement rates and are more likely to make in-app purchases, boosting your ROI.",
                icon: <FaLightbulb size={50} className="text-orange-400 mb-4 mx-auto" />,
              },
              {
                title: "Enhanced Security",
                desc: "Apple's stringent security measures provide a safer ecosystem for both users and your application's data.",
                icon: <FaShieldAlt size={50} className="text-red-400 mb-4 mx-auto" />,
              },
              {
                title: "Global Market Presence",
                desc: "Access to a global audience with strong purchasing power, especially in developed markets, through the Apple App Store.",
                icon: <FaMobileAlt size={50} className="text-blue-400 mb-4 mx-auto" />,
              },
            ].map((card, i) => (
              <motion.div
                key={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                transition={{ delay: i * 0.15 }}
                className="bg-blue-950 p-8 rounded-xl shadow-lg flex flex-col items-center hover:shadow-xl transform hover:-translate-y-2 transition-all duration-300 border border-orange-600"
              >
                {card.icon}
                <h3 className="text-xl font-semibold mb-3 mt-2 text-orange-600">{card.title}</h3>
                <p className="text-gray-400 leading-relaxed">{card.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 5 - Why Choose Nadezhda Venture for Your iOS App? */}
      <section className="bg-white text-white py-20 px-6 relative overflow-hidden">
       

        <div className="max-w-6xl mx-auto text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-orange-500">
            Why Partner with Us for Your <span className="text-orange-500">iOS App</span>?
          </h2>
          <div className="grid md:grid-cols-3 gap-10">
            {[
              {
                icon: <FaUsers size={50} className="text-orange-600 mb-4 mx-auto" />,
                title: "Expert iOS Developers",
                description: "Our team comprises seasoned iOS developers proficient in Swift and Objective-C, delivering native-level performance.",
              },
              {
                icon: <FaLightbulb size={50} className="text-white mb-4 mx-auto" />,
                title: "Innovation & Future-Proofing",
                description: "We stay abreast of the latest iOS trends and technologies, building apps that are cutting-edge and ready for tomorrow.",
              },
              {
                icon: <FaDollarSign size={50} className="text-orange-600 mb-4 mx-auto" />,
                title: "Timely & Cost-Effective",
                description: "We deliver high-quality iOS solutions efficiently and within budget, ensuring a great return on your investment.",
              },
            ].map((reason, i) => (
              <motion.div
                key={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                transition={{ delay: i * 0.15 }}
                className="bg-blue-950 backdrop-blur-sm p-8 rounded-xl shadow-lg flex flex-col items-center hover:shadow-xl transform hover:-translate-y-2 transition-all duration-300 border border-gray-700"
              >
                {reason.icon}
                <h3 className="text-xl font-semibold mb-3 mt-2 text-orange-500">{reason.title}</h3>
                <p className="text-gray-300 leading-relaxed">{reason.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      
      {/* SECTION 4 - Our Comprehensive iOS Development Services */}
      <section className="bg-gray-900 text-white py-20 px-6 relative overflow-hidden">
        {/* Subtle background graphic */}
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/black-linen.png')] opacity-10 pointer-events-none"></div>
        <div className="absolute inset-0 bg-gradient-to-tr from-gray-950 to-transparent opacity-50"></div>

        <div className="max-w-6xl mx-auto relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-orange-500">
            Our Comprehensive <span className="text-orange-500">iOS Development Services</span>
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: <FaMobileAlt size={30} className="text-blue-600" />,
                title: "Custom iOS App Development",
                description: "Building bespoke iPhone and iPad applications from concept to deployment, tailored to your unique business needs.",
              },
              {
                icon: <FaPalette size={30} className="text-blue-600" />,
                title: "iOS UI/UX Design",
                description: "Crafting beautiful and intuitive user interfaces with a focus on Apple's Human Interface Guidelines for optimal user experience.",
              },
              {
                icon: <FaCogs size={30} className="text-blue-600" />,
                title: "iOS App Modernization",
                description: "Upgrading and optimizing your existing iOS applications with modern features, improved performance, and enhanced security.",
              },
              {
                icon: <FaCode size={30} className="text-blue-600" />,
                title: "Enterprise iOS Solutions",
                description: "Developing robust, secure, and scalable iOS applications for large enterprises, focusing on complex integrations and business logic.",
              },
              {
                icon: <FaTools size={30} className="text-blue-600" />,
                title: "iOS App Testing & QA",
                description: "Rigorous quality assurance and testing across various Apple devices and iOS versions to ensure a bug-free and high-performing app.",
              },
              {
                icon: <FaHeadset size={30} className="text-blue-600" />,
                title: "App Store Submission & Support",
                description: "End-to-end guidance for App Store submission, along with ongoing support and maintenance services post-launch.",
              },
            ].map((service, i) => (
              <motion.div
                key={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                transition={{ delay: i * 0.1 }}
                className="bg-white p-6 rounded-lg shadow-md flex items-start space-x-4 hover:shadow-xl transition-shadow duration-300 border border-gray-700 hover:border-orange-500"
              >
                <div className="flex-shrink-0">{service.icon}</div>
                <div>
                  <h3 className="text-xl font-semibold mb-2 text-orange-600">{service.title}</h3>
                  <p className="text-gray-900 text-base">{service.description}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      

      {/* SECTION 6 - Client Testimonials */}
      {/* <section className="bg-gray-800 text-white py-20 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/diagmonds-light.png')] opacity-10 pointer-events-none"></div>

        <div className="max-w-6xl mx-auto text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-orange-500">
            What Our Clients Say About Our <span className="text-orange-500">iOS Apps</span>
          </h2>
          <div className="grid md:grid-cols-2 gap-10">
            {[
              {
                quote: "Nadezhda Venture delivered an exceptional iOS app that perfectly aligns with our brand vision. The performance is flawless, and the user interface is incredibly intuitive.",
                author: "Rohan Gupta",
                company: "Startup Founder",
              },
              {
                quote: "The iOS application developed by Nadezhda Venture has received rave reviews from our users. Their expertise and dedication truly set them apart in the market.",
                author: "Sakshi Verma",
                company: "Tech Lead, Enterprise Corp.",
              },
            ].map((testimonial, i) => (
              <motion.div
                key={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                transition={{ delay: i * 0.2 }}
                className="bg-gray-900/70 backdrop-blur-sm p-8 rounded-lg shadow-xl text-left border-l-4 border-orange-500 hover:shadow-2xl transition-shadow duration-300"
              >
                <p className="text-lg italic mb-6 text-gray-100">"{testimonial.quote}"</p>
                <p className="font-semibold text-orange-400">{testimonial.author}</p>
                <p className="text-gray-300 text-sm">{testimonial.company}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section> */}

      {/* SECTION 7 - Frequently Asked Questions (FAQ) */}
      <section className="bg-white text-white py-20 px-6 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/dot-grid.png')] opacity-10 pointer-events-none"></div>

        <div className="max-w-4xl mx-auto relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-center text-orange-500">
            Common Questions About <span className="text-orange-500">iOS App Development</span>
          </h2>
          <div className="space-y-6">
            {[
              {
                question: "Why should I develop an app for iOS?",
                answer: "iOS offers a premium user base with higher engagement and purchasing power. Developing for iOS ensures a high-quality user experience, strong security, and access to a lucrative market segment through the Apple App Store.",
              },
              {
                question: "How long does iOS app development take?",
                answer: "The timeline depends on the app's complexity, features, and specific requirements. A basic utility app might take 10-14 weeks, while a more intricate enterprise or consumer app could range from 4 to 9 months or more. We provide a detailed estimate after a thorough requirements analysis.",
              },
              {
                question: "What programming languages do you use for iOS?",
                answer: "We primarily use Swift and Objective-C for native iOS app development. These languages ensure optimal performance, security, and seamless integration with Apple's ecosystem. For cross-platform needs, we can also utilize Flutter or React Native.",
              },
              {
                question: "Do you provide post-launch support and updates?",
                answer: "Absolutely. Our commitment extends beyond launch. We offer comprehensive post-launch support, including bug fixes, performance monitoring, security updates, and feature enhancements to ensure your iOS application remains robust, secure, and compatible with the latest iOS versions.",
              },
            ].map((faq, i) => (
              <motion.div
                key={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.1 }}
                variants={fadeInUp}
                transition={{ delay: i * 0.1 }}
                className="bg-gray-300 p-6 rounded-lg shadow-md border border-gray-700"
              >
                <details className="group">
                  <summary className="flex justify-between items-center cursor-pointer text-lg font-semibold text-blue-900">
                    {faq.question}
                    <FaQuestionCircle className="text-orange-400 group-open:rotate-180 transition-transform duration-300" />
                  </summary>
                  <p className="mt-4 text-gray-800 leading-relaxed">{faq.answer}</p>
                </details>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      

     
    </>
  );
};

export default IosAppDevelopmentPage;